import { GridCard, SaleIcon, SkeletonLoader } from 'cat-ecommerce-alloy';
import {
  PAGE_BREAKPOINTS,
  FITMENT_VARIANTS,
  PRODUCT_VARIANTS,
  SCENE7_PRESETS,
  STATUS
} from '@app/constants/commonConstants';
import { useBreakpoint } from '@app/hooks';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ProductOverviewPriceDetails from '../../ProductOverview/ProductOverviewPriceDetails';
import styles from './ProductComparisonCard.module.scss';
import ProductOverviewAvailability from '../../ProductOverview/ProductOverviewAvailability';
import ProductFitment from '../../../ProductFitment';
import ProductLink from '../../ProductLink';
import CoreDeposit from '../../../../pages/pdp/ProductOverview/ProductPriceDetails/CoreDeposit';
import ProductPrimaryActions from '../../ProductPrimaryActions';
import ProductCardThumbnail from '../ProductCardThumbnail';
import ProductComparisonCardSpecifications from './ProductComparisonCardSpecifications';
import { renderSpecification } from './utils';
import Conditional from '../../../Conditional';
import { isCategoryPage, isEmpty, isSearchPage } from '@app/utils';
import useScrollIntoView from '@app/hooks/useScrollIntoView';
import { useEffect, useState } from 'react';
import ProductIcons from '../ProductIcons';
import {
  PLP_MUTATION_LOADER_SELECTOR,
  PLP_MUTATION_PRICE_SELECTOR,
  PRICE_AND_AVAILABILITY_API
} from '@app/constants/analyticsConstants';
import useMutationObserverLoaderTime from '../../../analytics/hooks/useMutationObserverLoaderTime';
import {
  specialPropType,
  attributesPropType,
  errorInfoProptype,
  partsImagesProptype
} from '../declarations.propTypes';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { isSpecialsPage } from '@app/components/pages/search-results/utils';
import { PCC_HEADLESS_PDP_FLAG } from '@app/constants/featureFlags';

const ProductComparisonCard = ({
  attributes,
  categoryPath: category,
  dataPage,
  didFitmentCallResolve,
  errorInfo,
  expanded,
  getFitmentCheck,
  getFitmentResult,
  handleProductClick,
  index,
  itemCatEntryId,
  itemPartNumber: partNumber,
  manufacturer,
  name,
  partsImages = [],
  preventProductReload,
  priceCallStatus,
  productDisplayUrl,
  scrollIntoView,
  setExpanded,
  shortDescription,
  showRowVariant = false,
  shouldShowFitmentData,
  specials,
  type,
  uniqueId,
  gaListName
}) => {
  const [disableScroll, setDisableScroll] = useState(false);
  const { search } = useLocation();
  const { searchType } = queryString.parse(search);
  const { ref, scroll } = useScrollIntoView();
  const uniqueIdentifier = uniqueId ?? partNumber;
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const pccSpecialsOnSearchAndCategoryPages = useSelector(
    s => s.featureFlag.PCC_SpecialsOnSearchAndCategoryPages
  );
  const isInstantAccess = useSelector(s => s.common?.isInstantAccess);
  const priceAlreadyLoaded = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]
  );
  const priceAndAvailabilityStatus = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.loadingStatus
  );
  const partPriceIsLoading =
    !priceAlreadyLoaded || priceAndAvailabilityStatus === STATUS.PENDING;
  const price = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.discountedPrice
  );
  const unformattedUnitPrice = useSelector(
    s =>
      s.products?.priceAndAvailability?.[uniqueIdentifier]?.unformattedUnitPrice
  );
  const unitPrice = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.unitPrice
  );
  const productWeight = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.weight
  );
  const availability = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.availability
  );
  const enableAddToCart = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.enableAddToCart
  );
  const indirectReplacement = useSelector(
    s =>
      s.products?.priceAndAvailability?.[uniqueIdentifier]?.indirectReplacement
  );
  const replacementParts = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.replacementParts
  );
  const isNonReturnable = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.nonReturnable
  );
  const productImageUrl = useSelector(
    s => s.products?.byId?.[uniqueIdentifier]?.imageURL
  );
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const isReman = useSelector(
    s => s.products?.priceAndAvailability?.[uniqueIdentifier]?.reman
  );
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );
  const taxDisclaimerMessage = useSelector(s => s.orders?.taxDisclaimerMessage);

  const esiteURL = useSelector(s => s.common?.esiteURL);
  const isPDPHeadlessFeatureFlagEnable = useSelector(
    s => s.featureFlag?.[PCC_HEADLESS_PDP_FLAG]
  );

  const urlBasedonFeatureFlag = isPDPHeadlessFeatureFlagEnable
    ? `${esiteURL}/product/${partNumber}`
    : productDisplayUrl;

  useEffect(() => {
    if (scrollIntoView && ref && !disableScroll) {
      scroll({ scrollOnce: true });
    }
  }, [disableScroll, ref, scroll, scrollIntoView]);

  const observableRef = useMutationObserverLoaderTime(
    uniqueIdentifier,
    PLP_MUTATION_LOADER_SELECTOR,
    PLP_MUTATION_PRICE_SELECTOR,
    PRICE_AND_AVAILABILITY_API
  );

  const productClicked = product => {
    setDisableScroll(true);
    handleProductClick(product);
  };
  const isPriceLoading = priceCallStatus === STATUS.PENDING;
  const isReplacedPart = !isEmpty(replacementParts) || indirectReplacement;
  const isZeroPrice = price === formattedZeroPrice;
  const showPriceAndAvailability =
    enableAddToCart &&
    price &&
    !isZeroPrice &&
    !isReplacedPart &&
    !isCatCorp &&
    isInstantAccess;
  const priceForProductLoading = preventProductReload
    ? isPriceLoading && partPriceIsLoading
    : isPriceLoading;
  const showCTA = !priceForProductLoading;

  const dealerCustomer = useSelector(s => s.dealerCustomer);
  const isKit = type === 'PackageBean';
  const isItemBeanOrProductBean = type === 'ItemBean' || type === 'ProductBean';
  const hasSpecials = !isEmpty(specials);
  let shouldDisplaySpecialsLabel = hasSpecials;
  let shouldDisplayPromoTag = hasSpecials;
  const shouldDisplaySpecialsAttributes =
    pccSpecialsOnSearchAndCategoryPages && hasSpecials;
  if (isSearchPage() && !isSpecialsPage(searchType)) {
    shouldDisplayPromoTag = shouldDisplaySpecialsAttributes;
    shouldDisplaySpecialsLabel = shouldDisplaySpecialsAttributes;
  } else if (isCategoryPage()) {
    shouldDisplaySpecialsLabel = shouldDisplaySpecialsAttributes;
  }
  const specsIconConditions = {
    isReman,
    isKit,
    isNonReturnable,
    isReplacedPart
  };

  const productClickDetails = {
    availability,
    brand: manufacturer,
    category,
    checkFitment:
      serialNumber && getFitmentCheck && getFitmentCheck(partNumber),
    name: name,
    partNumber,
    position: index + 1,
    price: unformattedUnitPrice,
    productWeight,
    relatedModel: model
  };
  const isXS = useBreakpoint(PAGE_BREAKPOINTS.XS);
  const dynamicSize = isXS ? '96px' : '72px';
  const productCardThumbnailURL = isItemBeanOrProductBean
    ? productImageUrl
    : partsImages;

  const getWrappedContent = (className, ...content) =>
    showRowVariant ? (
      <div className={className} key="comparison-card-overview">
        {content}
      </div>
    ) : (
      content
    );

  const getCardThumbnail = () => {
    const { name: specialsName = '' } = shouldDisplaySpecialsLabel
      ? (specials[0] ?? specials)
      : {};

    return (
      <ProductCardThumbnail
        alt={name}
        className={cx(
          {
            'me-3 mb-0': showRowVariant,
            'mb-2': !showRowVariant
          },
          'text-center d-flex'
        )}
        handleProductClick={productClicked}
        height={showRowVariant ? dynamicSize : '160px'}
        imageUrl={productCardThumbnailURL}
        preset={SCENE7_PRESETS.PDP2}
        productClickDetails={productClickDetails}
        gaListName={gaListName}
        width={showRowVariant ? dynamicSize : '160px'}
        url={urlBasedonFeatureFlag}
        key="ProductCardThumbnail"
        specialsName={specialsName}
      >
        {shouldDisplayPromoTag && (
          <SaleIcon
            className="u-fill-icon--CatYellow085 position-absolute"
            height={showRowVariant ? 16 : 24}
            width={showRowVariant ? 16 : 24}
            key={name}
          />
        )}
      </ProductCardThumbnail>
    );
  };

  const getCardName = () => (
    <ProductLink
      className="text-reset"
      gaListName={gaListName}
      handleProductClick={productClicked}
      link={urlBasedonFeatureFlag}
      key="comparison-card-name"
      productClickDetails={productClickDetails}
    >
      <h2
        className={cx(
          'h2 u-color--ExtraDarkGray u-fs--24',
          styles['product-card__name']
        )}
        title={name}
        data-page={dataPage}
        ref={ref}
      >
        {name}
      </h2>
    </ProductLink>
  );
  const getMainAttribute = () => {
    if (shortDescription) {
      return (
        <p
          className={cx(
            styles['product-card__description'],
            ' color-dark-gray',
            'mb-0',
            'text-sm'
          )}
          key="comparison-card-short-description"
        >
          {shortDescription}
        </p>
      );
    } else if (attributes?.length > 0) {
      return renderSpecification(attributes[0]);
    } else {
      return (
        <div
          key="comparison-card-short-description"
          data-testid="comparison-card-short-description-test"
        />
      );
    }
  };

  const getPrice = () => (
    <div ref={observableRef}>
      <Conditional
        fallback={
          <SkeletonLoader
            data-testid="product-card__skeleton-loader"
            className={cx(styles['product-card__skeleton-loader'], 'w-100')}
          />
        }
        test={!priceForProductLoading}
        key="comparison-card-price-details"
      >
        {showPriceAndAvailability ? (
          <>
            <ProductOverviewPriceDetails
              taxDisclaimerMessage={taxDisclaimerMessage}
              taxDisclaimerClassNames={'py-1'}
              className="mt-2"
              price={price}
              unitPrice={unitPrice}
              {...dealerCustomer}
            />
            <CoreDeposit productId={uniqueIdentifier} showSmallVariant />
          </>
        ) : (
          <div key="comparison-card-price-details" />
        )}
      </Conditional>
    </div>
  );

  const getCTA = () =>
    showCTA ? (
      <ProductPrimaryActions
        errorInfo={errorInfo}
        hideCheckPrice
        partNumber={partNumber}
        itemCatEntryId={itemCatEntryId}
        priceCallStatus={
          preventProductReload && isPriceLoading
            ? STATUS.RESOLVED
            : priceCallStatus
        }
        productClickDetails={productClickDetails}
        gaListName={gaListName}
        productId={uniqueIdentifier}
        quantity={1}
        url={urlBasedonFeatureFlag}
        variant={PRODUCT_VARIANTS.card}
        key="comparison-card-cta"
      />
    ) : (
      <div key="comparison-card-cta" />
    );

  const getAvailability = () =>
    showPriceAndAvailability ? (
      <ProductOverviewAvailability
        availability={availability}
        className="mt-1"
        key="comparison-card-availability"
      />
    ) : (
      <div key="comparison-card-availability" />
    );

  const getFitment = () =>
    shouldShowFitmentData ? (
      <ProductFitment
        className="mt-2"
        didFitmentCallResolve={didFitmentCallResolve}
        getFitmentResult={getFitmentResult}
        partNumber={partNumber}
        variant={FITMENT_VARIANTS.small}
        key="comparison-card-fitment"
      />
    ) : (
      <div key="comparison-card-fitment" />
    );

  const getSpecifications = () => (
    <ProductComparisonCardSpecifications
      attributes={attributes}
      expanded={expanded}
      manufacturer={manufacturer}
      setExpanded={setExpanded}
      shortDescription={shortDescription}
      key="comparison-card-specifications"
    />
  );

  const getProductIcons = () => <ProductIcons {...specsIconConditions} />;

  return (
    <GridCard
      key={uniqueIdentifier}
      childClassName={cx(styles['product-item'])}
      lastChildClassName="px-0"
      data-testid="grid-card"
    >
      {getWrappedContent(
        'd-flex flex-row',
        getCardThumbnail(),
        getWrappedContent(
          'w-100',
          getCardName(),
          getMainAttribute(),
          getPrice(),
          getCTA(),
          getAvailability(),
          getFitment(),
          getProductIcons(),
          getSpecifications()
        )
      )}
    </GridCard>
  );
};
ProductComparisonCard.propTypes = {
  attributes: attributesPropType,
  categoryPath: PropTypes.string,
  currentCategory: PropTypes.string,
  dataPage: PropTypes.number,
  didFitmentCallResolve: PropTypes.bool,
  errorInfo: errorInfoProptype,
  expanded: PropTypes.bool,
  getFitmentCheck: PropTypes.func,
  getFitmentResult: PropTypes.func,
  handleProductClick: PropTypes.func,
  index: PropTypes.number,
  itemCatEntryId: PropTypes.string,
  itemPartNumber: PropTypes.string,
  manufacturer: PropTypes.string,
  name: PropTypes.string,
  onScrollIntoView: PropTypes.func,
  partsImages: partsImagesProptype,
  priceCallStatus: PropTypes.string,
  preventProductReload: PropTypes.bool,
  productDisplayUrl: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  setExpanded: PropTypes.func,
  shortDescription: PropTypes.string,
  shouldShowFitmentData: PropTypes.bool,
  showRowVariant: PropTypes.bool,
  specials: specialPropType,
  type: PropTypes.string,
  uniqueId: PropTypes.string,
  gaListName: PropTypes.string
};

export default ProductComparisonCard;
